import { AuthorizationTileProvider } from './AuthorizationTileProvider';
import { AuthorizationSubdivide } from './AuthorizationSubdivide';
import { ISubdividableMissionFeature } from './model/ISubdividableMissionFeature';
import * as turf from '@turf/turf';
import { Feature, Polygon, MultiPolygon } from '@turf/turf';
import { AuthorizationFeatureCollection } from './AuthorizationFeatureCollection';
import { ISubdividableMissionProperties } from './model/ISubdividableMissionProperties';
import { PersistableMissionFeatureCollection } from './model/PersistableMissionFeatureCollection';
import { ILAANCStatusMeta } from './model/advisories/IAuthorizableLAANCAdvisoryProperties';
import { AuthorizableLAANCAdvisoryProperties } from './model/advisories/AuthorizableLAANCAdvisoryProperties';
import { GISDiffer } from './GISDiffer';
import { NearestPointFinder, PolygonWithCenter } from './support/NearestPointFinder';
import { INearbyAdvisoryProperties } from './model/advisories/INearbyAdvisoryProperties';
import { TilesClientLogger } from './support/TilesClientLogger';

export interface IMockAirspaceProvider {
    get: () => AuthorizationFeatureCollection;
}

export class AuthorizeMissionPolygonFacadeMockAirspaceProvider implements IMockAirspaceProvider{
    get = (): AuthorizationFeatureCollection => {
        return new AuthorizationFeatureCollection({ features: [] });
    }
}

// can change this with IOC.get(AuthorizeMissionFacadeConfig).BLOCK_CROP_TOLERANCE = .9
// but don't try and create a mission, b/c the API will still be whatever is hard coded
export class AuthorizeMissionFacadeConfig {
    BLOCK_CROP_TOLERANCE = .99;

    // if we still see some missions fails (in a block but we didn't think they were, then we will want to increase this more)
    PAD_FROM_BLOCK_METERS = .5;
}

export class AuthorizeMissionFacade {
    static inject = () => [
        AuthorizationTileProvider,
        AuthorizationSubdivide,
        AuthorizeMissionPolygonFacadeMockAirspaceProvider,
        GISDiffer,
        AuthorizeMissionFacadeConfig,
        NearestPointFinder,
        TilesClientLogger
    ];

    constructor(
        private authorizationTileProvider: AuthorizationTileProvider,
        private authorizationSubdivide: AuthorizationSubdivide,
        private mockAirspaceProvider: AuthorizeMissionPolygonFacadeMockAirspaceProvider,
        private gisDiffer: GISDiffer,
        private config: AuthorizeMissionFacadeConfig,
        private nearestPointFinder: NearestPointFinder,
        private logger: TilesClientLogger
    ) {}

    private subdivideWithTruncationRetry = async (mission: ISubdividableMissionProperties, initializedStatusMeta: ILAANCStatusMeta): Promise<PersistableMissionFeatureCollection> =>{
        try{
            return await this.buildRadiusAndSubdivide(mission, initializedStatusMeta);
        } catch (err: any){
            if (this.isErrorWeShouldRetry(err)){
                const truncatedCenter = turf.truncate({
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'Point',
                        coordinates: [mission.center.lng, mission.center.lat]
                    }
                }, { precision: 6});
                const [ lng, lat ] = truncatedCenter.geometry.coordinates;
                mission.center = { lng, lat };
                return await this.buildRadiusAndSubdivide(mission, initializedStatusMeta);
            }
            else {
                throw err;
            }
        }
    }

    private isErrorWeShouldRetry = (err: any) => {
        if (!err){
            return false;
        }
        if (err.innerError && err.innerError.toString().includes('Infinite loop when passing sweep line over endpoints')){
            return true;
        }
        if (err.innerError && err.innerError.toString().includes('Unable to find segment')){
            return true;
        }
        if (err.innerError && err.innerError.toString().includes('Maximum call stack size exceeded')){
            return true;
        }

        return false;
    }

    private addUASFMFeaturesThatAreNearlyWithinRadius = async ({ actual, mission, initializedStatusMeta }: { actual: PersistableMissionFeatureCollection, mission: ISubdividableMissionProperties, initializedStatusMeta: ILAANCStatusMeta }): Promise<void> => {
        // do it again with a slightly larger radius (padding)
        // in case there are some UASFM features that are nearly in the radius
        // because we have seen the FAA fail expecting a feature that is nearly, but not quite in our radius
        const padded = await this.subdivideWithTruncationRetry({
            ...mission,
            radiusMeters: mission.radiusMeters + 10
        }, initializedStatusMeta);

        const authFeatureToNaturalKey = (f: Feature<turf.Polygon, AuthorizableLAANCAdvisoryProperties>): string => `${f.properties.airspaceIdent}-${f.properties.startTime}-${f.properties.endTime}`;

        // NOTE: we're not checking for blocked on purpose (maybe the padded mission is blocked, eg in an NSUFR)
        //  we don't care that isn't he mission they are flying, we just want to learn what UASFM grid ids are
        //  nearly within the mission we are flying
        for (const actualAuthFeature of actual.getLAANCAuthorizeableAdvisoryFeatures()){
            const paddedAuthFeature = padded.getLAANCAuthorizeableAdvisoryFeatures().find(f => authFeatureToNaturalKey(f) === authFeatureToNaturalKey(actualAuthFeature));
            if (paddedAuthFeature){
                const actualUASFMGridIds = actualAuthFeature.properties.uasfmGridIdsV2.map(({ gridId }) => gridId);

                const uasfmGridsInPaddedButNotInActual = paddedAuthFeature.properties.uasfmGridIdsV2.filter(paddedGrid => !actualUASFMGridIds.includes(paddedGrid.gridId));
                if (uasfmGridsInPaddedButNotInActual.length > 0){
                    actualAuthFeature.properties.uasfmGridIdsV2 = [
                        ...actualAuthFeature.properties.uasfmGridIdsV2,
                        ...uasfmGridsInPaddedButNotInActual.map(props => ({
                            ...props,
                            fromPadding: true
                        }))
                    ];
                }
            }
            // NOTE: we are not handling the scenario where the padded radius adds an entirely new authorization
            //  but that is even more complex and we have never seen it happen, so we should wait to prioritize it
        }
    }

    subdivide = async (mission: ISubdividableMissionProperties, initializedStatusMeta: ILAANCStatusMeta): Promise<PersistableMissionFeatureCollection> =>{
        const actual = await this.subdivideWithTruncationRetry(mission, initializedStatusMeta);

        // we sometimes see failed missions because the missions is "barely" in block
        // such that we don't think it is in block, but the FAA does
        // to work around this, we can pad our mission by [10 meters], b/c otherwise we
        // don't see it, and if the mission has any blocks, then pad the blocks (to crop
        // our mission to be farther from the block)

        // produce a padded radius to see if there are any blocks that we should crop out of our original radius
        const padded = await this.subdivideWithTruncationRetry({
            ...mission,
            radiusMeters: mission.radiusMeters + 10
        }, initializedStatusMeta);

        // remove the blocks from the original radius, with some padding, so they aren't so close to the block that
        // they might fail authorization
        const blockingFeatures = padded.getAdvisoryFeatures().filter(f => f.properties.advisoryType === 'block');
        let radiusWithBlocksCroppedOut: Feature<Polygon | MultiPolygon, ISubdividableMissionProperties> = JSON.parse(JSON.stringify(actual.getMissionRadius()));
        for (const f of blockingFeatures){
            if (radiusWithBlocksCroppedOut){
                // this might resultin a MultiPolygon, which we do not support, but we check for that later
                radiusWithBlocksCroppedOut = this.gisDiffer.diff({ clipPolygonFrom: radiusWithBlocksCroppedOut, clipPolygon: turf.buffer(f, this.config.PAD_FROM_BLOCK_METERS, { units: 'meters' }) });
            }
        }

        const isLAANC = actual.getLAANCAuthorizeableAdvisoryFeatures().length > 0;

        const isOneRingPolygon = radiusWithBlocksCroppedOut && radiusWithBlocksCroppedOut.geometry.type === 'Polygon' && radiusWithBlocksCroppedOut.geometry.coordinates.length === 1;

        const isCroppedRadiusBigEnough = isOneRingPolygon && turf.area(radiusWithBlocksCroppedOut) >= this.config.BLOCK_CROP_TOLERANCE * turf.area(actual.features[0]);

        if (isCroppedRadiusBigEnough){
            actual.features = [
                radiusWithBlocksCroppedOut,
                ...actual.getAdvisoryFeatures().filter(f => f.properties.advisoryType !== 'block')
            ];
            if (isLAANC){
                for (const blockingFeature of blockingFeatures){
                    for (const laancFeature of actual.getLAANCAuthorizeableAdvisoryFeatures()){
                        const cropped = this.gisDiffer.diff({ clipPolygonFrom: laancFeature, clipPolygon: turf.buffer(blockingFeature, this.config.PAD_FROM_BLOCK_METERS, { units: 'meters' }) });
                        if (!cropped){
                            // likely to never happen
                            throw new Error('the entire laanc feature was cropped out, which is not yet supported');
                        }
                        // change the geometry to be slightly smaller if it was up close to a blocking feature
                        laancFeature.geometry = cropped.geometry;
                    }
                }
                // technically it is is possible that a laanc feature is no longer relevant (eg cropping out .5 meters cropped out the entire feature)
                // at which point we might fail b/c we sent a grid id that wasn't needed
                // but that is super edge case (lets wait to see it before we add the complexity)
            }
        }


        if (isLAANC){
            // TODO: are the auth features cached?
            await this.addUASFMFeaturesThatAreNearlyWithinRadius({ actual, mission, initializedStatusMeta });
        }

        const operationalAwarenessAdvisory = await this.getOperationalAwareness(actual.getMissionRadius());
        actual.features.push(operationalAwarenessAdvisory as any);

        return actual;
    }

    // TODO: get this to a class to maintain facade
    private getOperationalAwareness = async (actualMissionRadius: Feature<Polygon, ISubdividableMissionProperties>): Promise<Feature<Polygon, INearbyAdvisoryProperties>> => {
        const twoNauticalMilesInMeters = 3704;
        const twoMileMission = { ...actualMissionRadius.properties, radiusMeters: twoNauticalMilesInMeters };
        try {
            const result = await this.buildRadiusAndSubdivide(twoMileMission, { initializedSource: 'user-mission-creation'});
            return {
                type: 'Feature',
                geometry: result.getMissionRadius().geometry,
                properties: {
                    source: 'operational-awareness',
                    advisoryType: 'advise',
                    advisories: result.getAdvisoryFeatures()
                        .filter(a => ![
                            'uasfm+airspace', // every grid is not useful
                            // 'uasfm+authorizable', // this is likely going to be redundant with controlled airspace
                            // 'stadiums', // we do these another way, and we would need them to be points
                            'airspace+class-g', // b/c that is the lack of something to be aware of
                            // 'airspace', // because we return airspace-info and airspace is the cropped version of airspace-info that is not laanc
                        ].includes(a.properties.source))
                        .map(nearbyFeature => {
                            const missionCenter = turf.point([actualMissionRadius.properties.center.lng, actualMissionRadius.properties.center.lat]);
                            const missionForFindNearest: PolygonWithCenter = {
                                ...actualMissionRadius,
                                properties: {
                                    center: missionCenter
                                }
                            };
                            const nearestPoint = this.nearestPointFinder.findNearestPoint({ referencePolygon: missionForFindNearest, nearbyFeature });

                            return {
                                ...nearbyFeature,
                                properties: {
                                    ...nearbyFeature.properties,
                                    distanceMeters: nearestPoint.properties.distanceMeters,
                                    direction: nearestPoint.properties.direction,
                                    nearestPoint
                                }
                            };
                        })
                }
            };
        }
        catch (err: any){
            // fail gracefully (we don't need opererational awareness)

            // log to sentry so we know
            this.logger.error(`WARN: Was not able to compute operational awareness. Got Error ${err.toString()} for ${JSON.stringify(actualMissionRadius)}`);

            return {
                type: 'Feature',
                geometry: actualMissionRadius.geometry,
                properties: {
                    source: 'operational-awareness',
                    advisoryType: 'advise',
                    advisories: []
                }
            };

        }

    }

    private buildRadiusPolygon = (mission: ISubdividableMissionProperties): Feature<Polygon> => {
        const roundToNearestTenth = (num: number) => Math.round(num * 10) / 10;
        const radius = turf.circle([mission.center.lng, mission.center.lat], roundToNearestTenth(mission.radiusMeters), { units: 'meters', steps: 50 });
        return radius;
    }

    private buildSquarePolygon = (mission: ISubdividableMissionProperties): Feature<Polygon> => {
        const radius = this.buildRadiusPolygon(mission);
        const bbox = turf.bbox(radius);

        const bboxPolygon = turf.bboxPolygon(bbox);

        return bboxPolygon;
    }

    private buildRadiusAndSubdivide = async (mission: ISubdividableMissionProperties, initializedStatusMeta: ILAANCStatusMeta): Promise<PersistableMissionFeatureCollection> =>{
        let radius: Feature<Polygon>;
        if (mission.isSquare){
            radius = this.buildSquarePolygon(mission);
        } else {
            radius = this.buildRadiusPolygon(mission);
        }
        const missionRadius: ISubdividableMissionFeature = {
            ...radius,
            properties: {
                ...mission
            }
        };
        const mockAirspaceData = this.mockAirspaceProvider.get();
        const authorization = await this.authorizationTileProvider.getAuthorizationFeatures(missionRadius);

        authorization.features = [
            ...authorization.features,
            ...mockAirspaceData.features
        ];

        try{
            return this.authorizationSubdivide.subdivide({
                mission: missionRadius,
                authorization,
                initializedStatusMeta
            });
        } catch (err){
            const wrappedError = new Error(`failed to subdivide center ${JSON.stringify(mission.center)} radius ${mission.radiusMeters} meters\nInner Error: ${(err as Error).toString()}`);
            (wrappedError as any).innerError = err;
            throw wrappedError;
        }

    }
}

